import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useThemeColors from '../hooks/useThemeColors';
import PaginationBar from '../components/PaginationBar';
import LoadErrorCard from '../components/LoadErrorCard';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { format } from 'date-fns';
import { 
  Search, Scale, Loader2, Clock, CheckCircle, XCircle, 
  ChevronRight, Eye, ChevronLeft, RefreshCw
} from 'lucide-react';

const ESTADOS_RESOLUCION = {
  'EN_PROCESO': { label: 'En Proceso', color: 'bg-blue-100 text-blue-800', icon: Clock },
  'PENDIENTE_APROBACION': { label: 'Pendiente Aprobación', color: 'bg-amber-100 text-amber-800', icon: Clock },
  'APROBADO': { label: 'Aprobado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  'POSPUESTO': { label: 'Pospuesto', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const ResolucionesPage = () => {
  const navigate = useNavigate();
  const { api, hasRole } = useAuth();
  const themeColors = useThemeColors();
  
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [resoluciones, setResoluciones] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('__all__');
  
  const searchTimeoutRef = useRef(null);
  const autoRefreshRef = useRef(null);

  const esLegal = hasRole('LEGAL');

  const fetchResoluciones = useCallback(async (page = 1, silent = false, signal) => {
    if (!silent) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setLoadError(false);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      
      if (filtroEstado && filtroEstado !== '__all__') {
        params.append('estado', filtroEstado);
      }
      
      if (busqueda.trim()) {
        params.append('busqueda', busqueda.trim());
      }
      
      const response = await api.get(`/resoluciones?${params.toString()}`, { signal });
      
      setResoluciones(response.data.data || []);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total
      });
    } catch (err) {
      if (isCanceled(err)) return;
      console.error('Error fetching resoluciones:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, filtroEstado, busqueda]);

  // Carga inicial y cuando cambian filtros
  useAbortableLoad((signal) => {
    fetchResoluciones(1, false, signal);
  }, [filtroEstado]);

  // Búsqueda con debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    const controller = new AbortController();
    searchTimeoutRef.current = setTimeout(() => {
      fetchResoluciones(1, false, controller.signal);
    }, 400);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      controller.abort();
    };
  }, [busqueda]);

  // Auto-refresh cada 30 segundos cuando la página está visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Reiniciar el intervalo cuando vuelve a ser visible
        startAutoRefresh();
      } else {
        // Limpiar cuando no está visible
        if (autoRefreshRef.current) {
          clearInterval(autoRefreshRef.current);
        }
      }
    };

    const startAutoRefresh = () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
      
      autoRefreshRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchResoluciones(pagination.page, true); // silent refresh
        }
      }, 30000);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    startAutoRefresh();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [pagination.page, fetchResoluciones]);

  const getEstadoBadge = (estado) => {
    const config = ESTADOS_RESOLUCION[estado] || { label: estado, color: 'bg-gray-100 text-gray-800', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const handleVerResolucion = (resolucion) => {
    const reclamoId = resolucion.reclamo_id || resolucion.reclamos?.id;
    navigate(`/admin/reclamos/${reclamoId}/resolucion`);
  };

  const handlePageChange = (newPage) => {
    fetchResoluciones(newPage);
  };

  const handleManualRefresh = () => {
    fetchResoluciones(pagination.page, true);
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="resoluciones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Resoluciones</h1>
          <p className="text-slate-500 text-sm mt-1">
            {esLegal ? 'Resoluciones asignadas a usted' : 'Gestión de resoluciones de reclamos'}
            {pagination.total > 0 && <span className="ml-2">({pagination.total} total)</span>}
          </p>
        </div>
      </div>

      {/* Filters - En una sola línea */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por folio o reclamante..."
                className="pl-10"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                data-testid="busqueda-input"
              />
            </div>
            
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="estado-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los estados</SelectItem>
                {Object.entries(ESTADOS_RESOLUCION).map(([key, value]) => (
                  <SelectItem key={key} value={key}>{value.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="icon"
              onClick={handleManualRefresh}
              disabled={refreshing}
              title="Actualizar"
              className="flex-shrink-0"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data View */}
      {loading ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          </CardContent>
        </Card>
      ) : loadError ? (
        <LoadErrorCard onRetry={() => fetchResoluciones(pagination.page)} testId="resoluciones-load-error" />
      ) : resoluciones.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Scale className="w-12 h-12 mb-4 text-slate-300" />
              <p>{esLegal ? 'No hay resoluciones asignadas a usted' : 'No se encontraron resoluciones'}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Vista móvil - Cards */}
          <div className="md:hidden space-y-3">
            {resoluciones.map((resolucion) => {
              const reclamo = resolucion.reclamos;
              return (
                <Card 
                  key={resolucion.id}
                  className="border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer"
                  onClick={() => handleVerResolucion(resolucion)}
                  data-testid={`resolucion-card-${resolucion.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="font-mono font-bold text-slate-900">{reclamo?.folio}</p>
                        <p className="text-sm text-slate-600 truncate">{reclamo?.nombre_completo}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getEstadoBadge(resolucion.estado)}
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Versión:</span>
                        <span className="text-slate-800">{resolucion.version}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Fecha:</span>
                        <span className="text-slate-800">
                          {format(new Date(resolucion.created_at), 'dd/MM/yyyy HH:mm')}
                        </span>
                      </div>
                      {resolucion.aprobador_asignado?.nombre && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Aprobador:</span>
                          <span className="text-slate-800">{resolucion.aprobador_asignado.nombre}</span>
                        </div>
                      )}
                      {resolucion.usuarios?.nombre && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Creador:</span>
                          <span className="text-slate-800">{resolucion.usuarios.nombre}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Vista desktop - Tabla */}
          <Card className="border-slate-200 hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Folio</TableHead>
                      <TableHead>Reclamante</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Aprobador</TableHead>
                      <TableHead>Versión</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="hidden lg:table-cell">Creador</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resoluciones.map((resolucion) => {
                      const reclamo = resolucion.reclamos;
                      return (
                        <TableRow 
                          key={resolucion.id} 
                          className="table-row cursor-pointer"
                          onClick={() => handleVerResolucion(resolucion)}
                          data-testid={`resolucion-row-${resolucion.id}`}
                        >
                          <TableCell className="font-mono font-medium">
                            {reclamo?.folio}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {reclamo?.nombre_completo}
                          </TableCell>
                          <TableCell>
                            {getEstadoBadge(resolucion.estado)}
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate">
                            {resolucion.aprobador_asignado?.nombre || '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {resolucion.version}
                          </TableCell>
                          <TableCell className="text-sm">
                            {format(new Date(resolucion.created_at), 'dd/MM/yyyy HH:mm')}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {resolucion.usuarios?.nombre || '-'}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVerResolucion(resolucion);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          <PaginationBar pagination={pagination} onPageChange={handlePageChange} themeColors={themeColors} />
        </>
      )}
    </div>
  );
};

export default ResolucionesPage;
